import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAttendeeProfile,
  updateAttendeeProfile,
  lookupAttendeePass,
  fetchOrderAttendees,
  fetchMeetings,
  getPortalInvoiceDownloadUrl,
  AttendeeProfile,
  UpdateProfilePayload,
  OrderAttendeesResponse,
  MeetingItem,
  PortalAttendee,
  PortalExhibitor,
} from '../lib/api';
import { ScheduleXMeetingCalendar } from '../components/meetings/ScheduleXMeetingCalendar';
import { RequestMeetingModal } from '../components/meetings/RequestMeetingModal';
import { MeetingListTab } from '../components/meetings/MeetingListTab';
import { PortalAttendeeDirectory } from '../components/portal/PortalAttendeeDirectory';
import { PortalExhibitorDirectory } from '../components/portal/PortalExhibitorDirectory';
import { eventInfo } from '../data';
import {
  CheckCircle2,
  Clock,
  Sparkles,
  ShieldCheck,
  Building2,
  Briefcase,
  Layers,
  MapPin,
  Utensils,
  Target,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  CreditCard,
  FileCheck2,
  FileText,
  FileDown,
  Lock,
  Search,
  Ticket,
  Users,
  Copy,
  Check,
  Share2,
  Calendar,
} from 'lucide-react';
import confetti from 'canvas-confetti';

const INDUSTRY_SEGMENTS = [
  'Agrochemicals & Technical Formulations',
  'Biologicals & Bio-Stimulants',
  'Fertilizers & Specialized Plant Nutrition',
  'Seeds & Crop Genetics',
  'Agricultural Drones & Precision Tech',
  'Farm Machinery & Mechanization',
  'Micro-Irrigation & Water Technologies',
  'Supply Chain & Post-Harvest Storage',
  'Agri-Banking, Trade Finance & Export',
];

const BUSINESS_NATURES = [
  'Manufacturer / Primary Producer',
  'National / State Wholesaler & Distributor',
  'Retailer / Dealer Network',
  'Importer / International Trading House',
  'Contract Research (CRO) / Analytical Lab',
  'Farmer Producer Organization (FPO)',
  'Corporate Farming / Plantation Operator',
  'Government, Regulatory or PSU Delegate',
  'Venture Capital / Private Equity Investor',
];

const DIETARY_OPTIONS = [
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'jain', label: 'Jain Vegetarian' },
  { id: 'non_veg', label: 'Non-Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
];

function DelegateHotelsSection() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="text-xs font-bold text-[#0A3D27] uppercase tracking-wider block">
            Hospitality & Accommodation
          </span>
          <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mt-0.5">
            Recommended Delegate Hotels in BKC
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Preferential corporate rates are negotiated for registered IIAB exhibitors and delegates.
          </p>
        </div>
        <Link
          to="/venue"
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#FAF5EA] hover:bg-[#F5E8C7] border border-[#E6C687] text-[#785613] text-xs font-bold transition-colors w-fit"
        >
          <span>Venue & Transport Guide</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs">
        <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-3 shadow-xs hover:border-[#0A3D27]/30 transition-all">
          <span className="text-[10px] font-bold text-[#785613] bg-[#FAF5EA] border border-[#E6C687] px-2.5 py-0.5 rounded-full uppercase tracking-wider inline-block">
            Walking Distance (300m)
          </span>
          <h4 className="text-base font-bold text-slate-900">Trident Hotel, Bandra Kurla</h4>
          <p className="text-slate-600 leading-relaxed">
            5-Star luxury hotel located directly across JWCC. Features state-of-the-art business amenities
            and fine dining restaurants.
          </p>
          <div className="pt-2 text-slate-500 text-[11px]">
            Distance: 3 min walk to Pavilion 3
          </div>
        </div>

        <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-3 shadow-xs hover:border-[#0A3D27]/30 transition-all">
          <span className="text-[10px] font-bold text-[#0A3D27] bg-[#ECFDF5] border border-emerald-200 px-2.5 py-0.5 rounded-full uppercase tracking-wider inline-block">
            Walking Distance (500m)
          </span>
          <h4 className="text-base font-bold text-slate-900">Sofitel Mumbai BKC</h4>
          <p className="text-slate-600 leading-relaxed">
            French luxury hospitality in the heart of BKC, featuring lavish suites and conference networking
            lounges.
          </p>
          <div className="pt-2 text-slate-500 text-[11px]">
            Distance: 5 min walk to Pavilion 3
          </div>
        </div>

        <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-3 shadow-xs hover:border-[#0A3D27]/30 transition-all">
          <span className="text-[10px] font-bold text-[#785613] bg-[#FAF5EA] border border-[#E6C687] px-2.5 py-0.5 rounded-full uppercase tracking-wider inline-block">
            Short Drive (3.5 km)
          </span>
          <h4 className="text-base font-bold text-slate-900">Grand Hyatt Mumbai Hotel & Residences</h4>
          <p className="text-slate-600 leading-relaxed">
            Expansive 5-star property situated between BKC and Chhatrapati Shivaji Maharaj International Airport.
          </p>
          <div className="pt-2 text-slate-500 text-[11px]">
            Distance: 10 min drive to JWCC
          </div>
        </div>
      </div>
    </div>
  );
}

export function RegistrationCompletePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const orderId = searchParams.get('orderId') || '';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<AttendeeProfile | null>(null);

  // Multi-pass order state
  const [orderData, setOrderData] = useState<OrderAttendeesResponse | null>(null);
  const [allAttendees, setAllAttendees] = useState<Array<any>>([]);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [copiedCollectionCode, setCopiedCollectionCode] = useState(false);

  // Pass Lookup State
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Survey Form State
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industrySegment, setIndustrySegment] = useState('');
  const [businessNature, setBusinessNature] = useState('');
  const [primaryInterest, setPrimaryInterest] = useState('');
  const [dietaryPreference, setDietaryPreference] = useState('vegetarian');
  const [cityAndCountry, setCityAndCountry] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** True once we've stopped auto-checking and payment still isn't confirmed. */
  const [pollTimedOut, setPollTimedOut] = useState(false);

  // B2B Networking & Meetings State
  const [activePortalTab, setActivePortalTab] = useState<'badge' | 'meetings' | 'exhibitors' | 'delegates'>('badge');
  const [calendarViewMode, setCalendarViewMode] = useState<'calendar' | 'list'>('calendar');
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [targetUserForMeeting, setTargetUserForMeeting] = useState<any>(null);
  const [slotClickDateTime, setSlotClickDateTime] = useState<string | undefined>(undefined);

  // Query B2B Meetings for this attendee
  const { data: meetingsData } = useQuery({
    queryKey: ['meetings', profile?.id],
    queryFn: () => fetchMeetings(profile!.id),
    enabled: !!profile?.id,
  });
  const meetings = meetingsData?.meetings || [];

  const applyProfileToForm = (data: AttendeeProfile) => {
    setProfile(data);
    setJobTitle(data.jobTitle || '');
    setCompanyName(data.companyName || '');
    setIndustrySegment(data.industrySegment || INDUSTRY_SEGMENTS[0]);
    setBusinessNature(data.businessNature || BUSINESS_NATURES[0]);
    setPrimaryInterest(data.primaryInterest || '');
    setDietaryPreference(data.dietaryPreference || 'vegetarian');
    setCityAndCountry(data.cityAndCountry || '');
    setSaveSuccess(data.profileCompleted);
  };

  const loadProfile = async (profileToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAttendeeProfile(profileToken);
      applyProfileToForm(data);

      if (data.relatedAttendees && data.relatedAttendees.length > 0) {
        setAllAttendees(data.relatedAttendees);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to retrieve registration profile');
    } finally {
      setLoading(false);
    }
  };

  const loadOrder = async (targetOrderId: string, initialToken?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchOrderAttendees(targetOrderId);
      setOrderData(res);
      setAllAttendees(res.attendees);

      // Select active attendee
      let selected = res.attendees[0];
      if (initialToken) {
        const found = res.attendees.find((a) => a.profileToken === initialToken);
        if (found) selected = found;
      }

      if (selected) {
        applyProfileToForm(selected);
      }
    } catch (err: any) {
      console.warn('Failed to load order attendees by orderId, fallback to profile token', err);
      if (initialToken) {
        await loadProfile(initialToken);
      } else {
        setError(err.message || 'Failed to retrieve order passes');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      loadOrder(orderId, token);
    } else if (token) {
      loadProfile(token);
    } else {
      setLoading(false);
      setError(null);
    }
  }, [orderId, token]);

  // Auto-polling for payment confirmation if status is pending (M5 / G5).
  // Checks quickly at first, then more slowly, for ~3 minutes in total.
  useEffect(() => {
    if (!profile || profile.paymentStatus === 'paid') return;
    setPollTimedOut(false);

    let cancelled = false;
    let pollCount = 0;
    const fastPolls = 12; // 12 x 2.5s = 30s
    const maxPolls = 27; // + 15 x 10s = ~3 min total
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      pollCount++;
      try {
        if (orderId) {
          const res = await fetchOrderAttendees(orderId);
          const current = res.attendees.find((a) => a.profileToken === profile.profileToken) || res.attendees[0];
          if (current?.paymentStatus === 'paid') {
            setOrderData(res);
            setAllAttendees(res.attendees);
            applyProfileToForm(current);
            return;
          }
        } else if (token) {
          const data = await fetchAttendeeProfile(token);
          if (data?.paymentStatus === 'paid') {
            applyProfileToForm(data);
            return;
          }
        }
      } catch {
        // Silently retry
      }
      if (pollCount >= maxPolls) {
        if (!cancelled) setPollTimedOut(true);
        return;
      }
      timer = setTimeout(tick, pollCount < fastPolls ? 2500 : 10000);
    };

    timer = setTimeout(tick, 2500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [profile?.paymentStatus, orderId, token]);

  const handleSelectAttendee = async (selectedAttendee: any) => {
    if (!selectedAttendee.profileToken) return;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('token', selectedAttendee.profileToken);
      return p;
    });
    await loadProfile(selectedAttendee.profileToken);
  };

  const handleCopyLink = (targetToken: string) => {
    const url = `${window.location.origin}/my-pass?token=${targetToken}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(targetToken);
    setTimeout(() => {
      setCopiedToken(null);
    }, 2500);
  };

  const handleLookupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupQuery.trim()) return;

    setLookupLoading(true);
    setLookupError(null);
    setLookupMessage(null);
    try {
      const res = await lookupAttendeePass(lookupQuery.trim());
      if (res.attendee?.profileToken) {
        setLookupMessage(`Found pass for ${res.attendee.fullName}! Loading pass...`);
        setSearchParams({ token: res.attendee.profileToken });
        await loadProfile(res.attendee.profileToken);
      } else {
        setLookupMessage(res.message || 'If an attendee pass is registered to this email, your badge access link has been dispatched to your inbox.');
      }
    } catch (err: any) {
      setLookupError(
        err.message || 'No attendee pass found matching your search. Please check your email or pass ID.'
      );
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => {
    if (profile && !profile.profileCompleted) {
      try {
        confetti({
          particleCount: 70,
          spread: 60,
          origin: { y: 0.5 },
          colors: ['#0A3D27', '#C5A059', '#155e3b'],
        });
      } catch {
        // Safe fallback
      }
    }
  }, [profile]);

  const handleSurveySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.profileToken) return;

    setSaving(true);
    setSaveError(null);
    try {
      const payload: UpdateProfilePayload = {
        jobTitle: jobTitle.trim() || undefined,
        companyName: companyName.trim() || undefined,
        industrySegment: industrySegment.trim() || undefined,
        businessNature: businessNature.trim() || undefined,
        primaryInterest: primaryInterest.trim() || undefined,
        dietaryPreference: dietaryPreference.trim() || undefined,
        cityAndCountry: cityAndCountry.trim() || undefined,
      };

      const result = await updateAttendeeProfile(profile.profileToken, payload);
      setProfile(result.attendee);
      setSaveSuccess(true);
      try {
        confetti({
          particleCount: 90,
          spread: 75,
          origin: { y: 0.6 },
          colors: ['#0A3D27', '#C5A059', '#10b981'],
        });
      } catch {
        // Safe fallback
      }
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update profile survey');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center space-y-4">
        <div className="w-12 h-12 border-4 border-[#0A3D27]/20 border-t-[#0A3D27] rounded-full animate-spin mx-auto" />
        <p className="text-sm text-slate-600">Retrieving attendee credentials and order passes...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-5xl mx-auto py-12 px-4 space-y-12 animate-in fade-in duration-300">
        <div className="max-w-xl mx-auto space-y-8">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-200 text-[#0A3D27] flex items-center justify-center mx-auto shadow-2xs">
              <Ticket className="w-8 h-8" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Access Your Visitor Pass</h1>
            <p className="text-sm text-slate-600 max-w-lg mx-auto leading-relaxed">
              Enter your registered email address or Pass ID to view your digital badge, download your QR pass, and
              complete your B2B matchmaking survey.
            </p>
          </div>

          {lookupError && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs text-rose-800 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
              <span>{lookupError}</span>
            </div>
          )}

          {lookupMessage && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs text-[#0A3D27] flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#0A3D27] shrink-0" />
              <span>{lookupMessage}</span>
            </div>
          )}

          {error && !lookupError && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-700 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Card: Lookup by Email or Pass ID */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
            <div className="space-y-1">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Search className="w-4 h-4 text-[#0A3D27]" />
                <span>Find Your Registration Pass</span>
              </h2>
              <p className="text-xs text-slate-500">
                Search using your email (e.g. <code className="text-[#0A3D27] font-semibold">name@example.com</code>) or your
                Pass ID (e.g. <code className="text-[#0A3D27] font-semibold">IIAB26-...</code>).
              </p>
            </div>

            <form onSubmit={handleLookupSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Registered Email Address or Pass ID</label>
                <input
                  type="text"
                  required
                  placeholder="Enter email or Pass ID..."
                  value={lookupQuery}
                  onChange={(e) => setLookupQuery(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                />
              </div>

              <button
                type="submit"
                disabled={lookupLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-950/20 cursor-pointer disabled:opacity-50"
              >
                {lookupLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Searching Attendee Registry...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>Lookup Pass & Digital Badge</span>
                  </>
                )}
              </button>
            </form>

            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <span>Don't have a visitor pass yet?</span>
              <Link to="/register" className="text-[#0A3D27] hover:underline font-semibold flex items-center gap-1">
                <span>Book Pass Now</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Hospitality & Accommodation Cluster for Visitors */}
        <div className="pt-8 border-t border-slate-200/80">
          <DelegateHotelsSection />
        </div>
      </div>
    );
  }

  const isPaid = profile.paymentStatus === 'paid';
  const hasMultipleAttendees = allAttendees && allAttendees.length > 1;
  const collectionCode = (((profile as any).collectionCode as string) || profile.id.slice(0, 6)).toUpperCase();
  const zohoInvoiceNumber = (profile as any).zohoInvoiceNumber || (orderData as any)?.zohoInvoiceNumber || null;

  const handleEnterMyIiabPortal = () => {
    const activeToken = token || profile.profileToken || '';
    sessionStorage.setItem('iiab_portal_email', profile.email);
    sessionStorage.setItem('iiab_participant_email', profile.email);
    if (activeToken) {
      sessionStorage.setItem('iiab_portal_token', activeToken);
    }
    navigate(
      `/my-iiab?token=${encodeURIComponent(activeToken)}&email=${encodeURIComponent(profile.email)}`
    );
  };

  const handleCopyCollectionCode = () => {
    navigator.clipboard.writeText(collectionCode);
    setCopiedCollectionCode(true);
    setTimeout(() => setCopiedCollectionCode(false), 2500);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {/* Top Success Banner */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 relative overflow-hidden shadow-sm">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-3">
            <div
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border ${
                isPaid ? 'bg-emerald-50 border-emerald-200 text-[#0A3D27]' : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}
            >
              {isPaid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              <span>
                {!isPaid
                  ? 'Confirming your payment…'
                  : hasMultipleAttendees
                  ? `Group Order Confirmed • ${allAttendees.length} Passes Issued`
                  : `Registration Order Confirmed • ${eventInfo.edition}`}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Welcome, {profile.fullName}!
            </h1>

            <p className="text-sm text-slate-600 max-w-xl leading-relaxed">
              {isPaid ? (
                <>
                  Your delegate pass for the <strong>India International Agri Business Conference &amp; Expo (IIAB 2027)</strong> has been
                  provisioned. Present your 6-character desk collection code at Pavilion 3 to pick up your printed 4×6 QR &amp; NFC badge.
                </>
              ) : (
                <>
                  We're waiting for the payment gateway to confirm your payment. This usually takes a few seconds.
                  Your collection code and confirmation email will be finalized as soon as it's confirmed.
                </>
              )}
            </p>

            {!isPaid && pollTimedOut && (
              <div
                id="payment-confirmation-delayed"
                className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm space-y-2 max-w-xl"
              >
                <p className="font-semibold">Confirmation is taking longer than usual.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>
                    <strong>Please don't pay again.</strong> If money left your account, your pass is safe – we'll email
                    your badge credentials as soon as the bank confirms.
                  </li>
                  <li>You can close this page. Use the link in your email, or come back and look up your pass by email.</li>
                  <li>
                    Still nothing after 30 minutes? Contact event support with order ID{' '}
                    <code className="font-mono">{orderId || '—'}</code>.
                  </li>
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    setPollTimedOut(false);
                    if (orderId) loadOrder(orderId, token);
                    else if (token) loadProfile(token);
                  }}
                  className="mt-1 py-1.5 px-4 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg cursor-pointer"
                >
                  Check again
                </button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 pt-1">
              <span className="flex items-center gap-1.5 bg-slate-50 px-3 py-1 rounded-lg border border-slate-200">
                <ShieldCheck className="w-3.5 h-3.5 text-[#0A3D27]" />
                Pass Tier: <strong className="text-slate-900 uppercase ml-1">{profile.tier.replace('_', ' ')}</strong>
              </span>

              <span className="flex items-center gap-1.5 bg-slate-50 px-3 py-1 rounded-lg border border-slate-200">
                <Clock className={`w-3.5 h-3.5 ${isPaid ? 'text-[#0A3D27]' : 'text-amber-600 animate-spin'}`} />
                Status:{' '}
                <strong className={`ml-1 ${isPaid ? 'text-[#0A3D27]' : 'text-amber-800'}`}>
                  {isPaid ? 'PAID & CONFIRMED' : 'Confirming Payment with Gateway...'}
                </strong>
              </span>

              <span className="flex items-center gap-1.5 bg-[#FAF5EA] px-3 py-1 rounded-lg border border-[#E6C687] font-mono text-xs font-black text-[#785613]">
                <CreditCard className="w-3.5 h-3.5 text-[#785613]" />
                Desk Code: {collectionCode}
              </span>
            </div>

            {/* GST Tax Invoice Status / Download Card (Decision D2) */}
            <div className="pt-2 flex flex-wrap items-center gap-3">
              {zohoInvoiceNumber ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-semibold text-[#0A3D27]">
                  <span>✅ GST Tax Invoice #{zohoInvoiceNumber} generated &amp; emailed to {profile.email}</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FAF5EA] border border-[#E6C687] text-xs font-semibold text-[#785613]">
                  <span>⏳ GST Tax Invoice queued — arriving in your inbox via Zoho Books (SAC 998596 • 18% GST)</span>
                </div>
              )}

              {(isPaid || zohoInvoiceNumber) && (
                <a
                  href={getPortalInvoiceDownloadUrl(
                    profile.id || orderId || '',
                    token || profile.profileToken || ''
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 text-xs font-bold shadow-2xs transition-colors"
                >
                  <FileDown className="w-3.5 h-3.5 text-[#0A3D27]" />
                  <span>Download GST Tax Invoice</span>
                </a>
              )}
            </div>
          </div>

          <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-2.5">
            <button
              type="button"
              onClick={handleEnterMyIiabPortal}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-extrabold transition-all shadow-md shadow-emerald-950/20 cursor-pointer"
            >
              <span>Enter My IIAB Portal →</span>
            </button>
            <button
              onClick={() => {
                if (orderId) loadOrder(orderId, profile.profileToken);
                else loadProfile(profile.profileToken);
              }}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 text-xs font-semibold transition-all cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh Status
            </button>
          </div>
        </div>
      </div>

      {/* MY IIAB PORTAL FEATURE SHOWCASE & SEAMLESS AUTO-SIGN-IN (Decisions D2 & D3) */}
      <div className="bg-gradient-to-br from-[#0A3D27] via-[#062B1B] to-slate-900 text-white rounded-3xl p-6 sm:p-8 border border-emerald-800/60 shadow-lg relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-64 h-64 bg-[#E6C687]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-emerald-800/60 pb-5">
            <div className="space-y-1.5">
              <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#FAF5EA]/10 border border-[#E6C687]/40 text-[#E6C687] text-[11px] font-bold uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-[#E6C687]" />
                <span>Delegate Digital Companion</span>
              </span>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                Explore Your My IIAB Portal
              </h2>
              <p className="text-xs text-emerald-100/80 max-w-2xl">
                One-click passwordless access to your personalized IIAB 2027 networking command center, hospitality vouchers, and B2B schedule.
              </p>
            </div>

            <button
              type="button"
              onClick={handleEnterMyIiabPortal}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#FAF5EA] hover:bg-[#F5E8C7] text-[#0A3D27] font-black text-xs sm:text-sm shadow-lg transition-all cursor-pointer"
            >
              <span>Enter My IIAB Portal →</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-[#E6C687]">
                <Calendar className="w-4 h-4" />
              </div>
              <h3 className="font-bold text-white text-sm">1. 1-on-1 B2B Meeting Scheduler</h3>
              <p className="text-emerald-100/75 text-[11px] leading-relaxed">
                Book confirmed 30-minute meetings with exhibitors and buyers across 27–28 July 2027.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-[#E6C687]">
                <Building2 className="w-4 h-4" />
              </div>
              <h3 className="font-bold text-white text-sm">2. Exhibitor &amp; Attendee Directories</h3>
              <p className="text-emerald-100/75 text-[11px] leading-relaxed">
                Search 150+ exhibiting brands and 5,000+ trade delegates by sector and sourcing interest.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-[#E6C687]">
                <Utensils className="w-4 h-4" />
              </div>
              <h3 className="font-bold text-white text-sm">3. Meal &amp; Lounge Perks Wallet</h3>
              <p className="text-emerald-100/75 text-[11px] leading-relaxed">
                View your allocated luncheon vouchers, VIP lounge access, and hospitality entitlements.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-[#E6C687]">
                <Lock className="w-4 h-4" />
              </div>
              <h3 className="font-bold text-white text-sm">4. Contact Privacy Controls</h3>
              <p className="text-emerald-100/75 text-[11px] leading-relaxed">
                Manage DPDP-compliant contact sharing and export two-way badge scan connections.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ATTENDEE PORTAL NAVIGATION TABS */}
      <div className="flex border-b border-slate-200 gap-2 overflow-x-auto">
        <button
          type="button"
          onClick={() => setActivePortalTab('badge')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors ${
            activePortalTab === 'badge'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Ticket className="w-4 h-4" />
          <span>My Badge & Pass</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('meetings')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors ${
            activePortalTab === 'meetings'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>My B2B Meetings ({meetings.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('exhibitors')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors ${
            activePortalTab === 'exhibitors'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Exhibitor Directory & Stalls</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('delegates')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors ${
            activePortalTab === 'delegates'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Delegate Networking</span>
        </button>
      </div>

      {/* TAB: B2B MEETINGS & SCHEDULE */}
      {activePortalTab === 'meetings' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">My Expo Schedule & 1-on-1 Meetings</h2>
              <p className="text-xs text-slate-500">
                View your confirmed meetings with exhibitors and trade delegates across the 2 event days (27–28 July 2027).
              </p>
            </div>

            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
              <button
                type="button"
                onClick={() => setCalendarViewMode('calendar')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                  calendarViewMode === 'calendar' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Calendar View
              </button>
              <button
                type="button"
                onClick={() => setCalendarViewMode('list')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                  calendarViewMode === 'list' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                List View ({meetings.length})
              </button>
            </div>
          </div>

          {calendarViewMode === 'calendar' ? (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-[#0A3D27] flex items-center justify-between">
                <span>💡 Click on any open slot to schedule a meeting with an exhibitor or peer delegate.</span>
                <span className="font-semibold">Expo Hours: 10:00 - 18:00</span>
              </div>
              <ScheduleXMeetingCalendar
                meetings={meetings}
                onSlotClick={(dt) => {
                  setSlotClickDateTime(dt);
                  setActivePortalTab('exhibitors');
                }}
              />
            </div>
          ) : (
            <MeetingListTab
              meetings={meetings}
              currentUserId={profile.id}
              onScheduleNew={() => setActivePortalTab('exhibitors')}
            />
          )}
        </div>
      )}

      {/* TAB: EXHIBITOR DIRECTORY */}
      {activePortalTab === 'exhibitors' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Exhibitors & Booth Stalls</h2>
            <p className="text-xs text-slate-500">
              Browse exhibiting companies, locate them on the floorplan, and schedule 30-minute meetings at their stall.
            </p>
          </div>
          <PortalExhibitorDirectory
            onScheduleMeeting={(ex) => {
              setTargetUserForMeeting({
                type: 'exhibitor',
                id: ex.reservationId,
                name: ex.fasciaName || ex.companyName,
                org: ex.companyName,
                email: ex.contactEmail,
                stallNumber: ex.boothId,
              });
              setIsMeetingModalOpen(true);
            }}
          />
        </div>
      )}

      {/* TAB: DELEGATE NETWORKING */}
      {activePortalTab === 'delegates' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Trade Visitor & Delegate Networking</h2>
            <p className="text-xs text-slate-500">
              Discover verified attendees and arrange 1-on-1 networking discussions in the Hall 3 B2B Lounge.
            </p>
          </div>
          <PortalAttendeeDirectory
            currentUserId={profile.id}
            onScheduleMeeting={(att) => {
              setTargetUserForMeeting({
                type: 'attendee',
                id: att.id,
                name: att.fullName,
                org: att.companyName || att.jobTitle || 'Trade Visitor',
                email: 'attendee@iiab-delegate.org', // masked email
              });
              setIsMeetingModalOpen(true);
            }}
          />
        </div>
      )}

      {/* TAB: MY BADGE & PASS (Original view) */}
      {activePortalTab === 'badge' && (
        <div className="space-y-10">

      {/* Multi-Attendee Order Navigator (if order has >1 passes) */}
      {hasMultipleAttendees && (
        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-5 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 text-[#0A3D27] flex items-center justify-center">
                <Users className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">
                  Passes in this Order ({allAttendees.length} Delegates)
                </h3>
                <p className="text-xs text-slate-500">
                  Click an attendee to view their digital badge or copy their direct pass URL.
                </p>
              </div>
            </div>

            {profile.companyName && (
              <span className="text-xs text-slate-600 bg-slate-50 px-3 py-1 rounded-lg border border-slate-200 font-medium">
                {profile.companyName}
              </span>
            )}
          </div>

          {/* Attendee Selector Pills */}
          <div className="flex flex-wrap gap-2">
            {allAttendees.map((att, idx) => {
              const isSelected = att.profileToken === profile.profileToken;

              return (
                <button
                  key={att.id || idx}
                  type="button"
                  onClick={() => handleSelectAttendee(att)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    isSelected
                      ? 'bg-[#0A3D27] text-white shadow-md shadow-emerald-950/20'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
                  }`}
                >
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px]">
                    {idx + 1}
                  </span>
                  <span>{att.fullName}</span>
                  <span
                    className={`text-[9px] uppercase px-1.5 py-0.2 rounded-full font-black ${
                      isSelected
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {att.tier.replace('_', ' ')}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Direct Share Links Table */}
          <div className="bg-[#F8F9F6] border border-slate-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-slate-700">
              <span className="flex items-center gap-1.5">
                <Share2 className="w-3.5 h-3.5 text-[#0A3D27]" />
                Share Passes with Registered Attendees
              </span>
              <span className="text-[11px] font-normal text-slate-500">
                Each link gives direct smartphone badge access
              </span>
            </div>

            <div className="divide-y divide-slate-200">
              {allAttendees.map((att, idx) => {
                const isCopied = copiedToken === att.profileToken;
                const attCollectionCode = (((att as any).collectionCode as string) || att.id?.slice(0, 6) || 'ISSUED').toUpperCase();

                return (
                  <div
                    key={att.id || idx}
                    className="py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                  >
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <strong className="text-slate-900">{att.fullName}</strong>
                        <span className="text-[10px] text-slate-500 font-mono">({att.email})</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span className="font-mono font-bold text-[#0A3D27]">Desk Code: {attCollectionCode}</span>
                        <span>•</span>
                        <span className="capitalize">{att.tier.replace('_', ' ')}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleCopyLink(att.profileToken)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          isCopied
                            ? 'bg-emerald-100 text-[#0A3D27] border border-emerald-300'
                            : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-2xs'
                        }`}
                      >
                        {isCopied ? (
                          <>
                            <Check className="w-3.5 h-3.5 text-[#0A3D27]" />
                            <span>Link Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" />
                            <span>Copy Direct Pass URL</span>
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSelectAttendee(att)}
                        className="px-2.5 py-1.5 text-xs text-[#0A3D27] hover:underline font-bold cursor-pointer"
                      >
                        View Pass →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Grid: Desk Collection Credential Card + Profile Survey */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: High-Contrast Desk Collection Card (Strict Physical QR Policy D1 — NO QR Code) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="space-y-2">
            <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
              Official Desk Collection Credential
            </h2>
            <p className="text-xs text-slate-500">
              Present this 6-character code + photo ID at the Registration Desk in Pavilion 3 to collect your physical 4×6 QR &amp; NFC Badge.
            </p>
          </div>

          <div className="sticky top-24">
            <div className="rounded-3xl bg-gradient-to-b from-[#0A3D27] via-[#062B1B] to-slate-900 p-1 shadow-2xl text-white">
              <div className="bg-slate-900/95 rounded-[22px] p-6 sm:p-7 border border-emerald-500/20 space-y-6 relative overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 block">
                      IIAB 2027 • 5th Edition
                    </span>
                    <span className="text-xs font-bold text-white">
                      27–28 July 2027 • JWCC Mumbai
                    </span>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#FAF5EA] text-[#785613] border border-[#E6C687]">
                    {profile.tier.replace('_', ' ')}
                  </span>
                </div>

                {/* Attendee Identity */}
                <div className="space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Registered Delegate
                  </div>
                  <h3 className="text-2xl font-black text-white tracking-tight">
                    {profile.fullName}
                  </h3>
                  {(profile.jobTitle || jobTitle) && (
                    <p className="text-xs font-medium text-slate-300">
                      {profile.jobTitle || jobTitle}
                    </p>
                  )}
                  {(profile.companyName || companyName) && (
                    <p className="text-xs font-bold text-emerald-400">
                      {profile.companyName || companyName}
                    </p>
                  )}
                </div>

                {/* High-Contrast 6-Character Collection Code Box */}
                <div className="bg-[#FAF5EA] text-slate-900 rounded-2xl p-5 border-2 border-[#E6C687] text-center space-y-2 shadow-inner">
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#785613] block">
                    6-Character Desk Collection Code
                  </span>
                  <div className="text-3xl sm:text-4xl font-mono font-black tracking-[0.25em] text-[#0A3D27] select-all">
                    {collectionCode}
                  </div>
                  <div className="pt-1 flex justify-center">
                    <button
                      type="button"
                      onClick={handleCopyCollectionCode}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white hover:bg-slate-100 border border-[#E6C687] text-[11px] font-bold text-[#785613] transition-colors cursor-pointer"
                    >
                      {copiedCollectionCode ? (
                        <>
                          <Check className="w-3 h-3 text-[#0A3D27]" />
                          <span>Copied Code!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy Collection Code</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Strict Physical QR Policy Notice */}
                <div className="p-3.5 rounded-xl bg-amber-950/50 border border-amber-700/50 text-amber-200 text-xs leading-relaxed">
                  <strong className="text-amber-300 block mb-0.5">Physical 4×6 QR &amp; NFC Badge Pickup:</strong>
                  Present this 6-character code + photo ID at the Registration Desk in Pavilion 3 to collect your physical 4×6 QR &amp; NFC Badge.
                </div>

                {/* Venue Footer */}
                <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Pavilion 3, JWCC, BKC, Mumbai</span>
                  </span>
                  <span className="font-mono text-emerald-400 font-semibold">Express Desk</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Post-Payment Matchmaking & Logistics Survey */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
            <div className="space-y-1.5 border-b border-slate-100 pb-5">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A3D27] uppercase tracking-wider">
                <FileCheck2 className="w-3.5 h-3.5" />
                Badge Customization • {profile.fullName}
              </div>
              <h2 className="text-xl font-black text-slate-900">
                Finalize Badge & Matchmaking Profile
              </h2>
              <p className="text-xs text-slate-500 leading-relaxed">
                Provide executive designation and sourcing requirements for this pass so our AI Matchmaking Engine
                can connect you with relevant manufacturers and export trade booths during the expo.
              </p>
            </div>

            {saveSuccess && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3 text-xs text-[#0A3D27]">
                <CheckCircle2 className="w-5 h-5 text-[#0A3D27] shrink-0" />
                <div>
                  <p className="font-bold">Matchmaking Profile Finalized!</p>
                  <p className="text-emerald-700 text-[11px]">
                    Your designation and sourcing interests have been written to your official pass lanyard credential.
                  </p>
                </div>
              </div>
            )}

            {saveError && (
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-center gap-3 text-xs text-rose-800">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
                <div>
                  <p className="font-bold">Error Updating Profile</p>
                  <p className="text-rose-700 text-[11px]">{saveError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSurveySubmit} className="space-y-5">
              {/* Job Title / Designation */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                  Executive Title / Designation
                </label>
                <input
                  type="text"
                  required
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Director of Agrochemical Sourcing, VP R&D"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                />
              </div>

              {/* Organization / Company */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  Organization / Company Name
                </label>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Mahavira Agrochemicals Ltd"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                />
              </div>

              {/* Grid: Industry Segment & Nature of Business */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-slate-400" />
                    Industry Segment
                  </label>
                  <select
                    value={industrySegment}
                    onChange={(e) => setIndustrySegment(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                  >
                    {INDUSTRY_SEGMENTS.map((seg) => (
                      <option key={seg} value={seg}>
                        {seg}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    Nature of Business
                  </label>
                  <select
                    value={businessNature}
                    onChange={(e) => setBusinessNature(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                  >
                    {BUSINESS_NATURES.map((nat) => (
                      <option key={nat} value={nat}>
                        {nat}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* City and Country */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  Operating Base (City & Country)
                </label>
                <input
                  type="text"
                  required
                  value={cityAndCountry}
                  onChange={(e) => setCityAndCountry(e.target.value)}
                  placeholder="e.g. Pune, India or Nairobi, Kenya"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                />
              </div>

              {/* Primary Sourcing & Matchmaking Interests */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5 text-slate-400" />
                  Primary Sourcing & Partnership Goal
                </label>
                <textarea
                  rows={2}
                  value={primaryInterest}
                  onChange={(e) => setPrimaryInterest(e.target.value)}
                  placeholder="e.g. Seeking technical active ingredients, biological adjuvant suppliers, and OEM toll formulators for Kharif 2028."
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                />
              </div>

              {/* Dietary Preference for Conference Pass Luncheon */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Utensils className="w-3.5 h-3.5 text-slate-400" />
                  Catering & Luncheon Dietary Preference
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {DIETARY_OPTIONS.map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center justify-center p-2.5 rounded-xl border text-xs font-semibold cursor-pointer transition-all ${
                        dietaryPreference === opt.id
                          ? 'bg-emerald-50 border-[#0A3D27] text-[#0A3D27]'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="dietary"
                        value={opt.id}
                        checked={dietaryPreference === opt.id}
                        onChange={() => setDietaryPreference(opt.id)}
                        className="sr-only"
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Submit CTA */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-3.5 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white font-extrabold text-xs shadow-md shadow-emerald-950/20 flex items-center justify-center gap-2 transition-all cursor-pointer"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      <span>Saving Matchmaking Profile...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Finalize Badge Credentials</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Hospitality & Accommodation */}
          <div className="pt-6 border-t border-slate-200/80">
            <DelegateHotelsSection />
          </div>
        </div>
      </div>
      </div>
      )}

      {/* REQUEST MEETING MODAL */}
      {profile && targetUserForMeeting && (
        <RequestMeetingModal
          isOpen={isMeetingModalOpen}
          onClose={() => {
            setIsMeetingModalOpen(false);
            setTargetUserForMeeting(null);
          }}
          currentUser={{
            type: 'attendee',
            id: profile.id,
            name: profile.fullName,
            org: profile.companyName || profile.jobTitle || undefined,
            email: profile.email,
          }}
          targetUser={targetUserForMeeting}
          initialDateTime={slotClickDateTime}
        />
      )}
    </div>
  );
}

export default RegistrationCompletePage;
